import { type DistanceUnit } from 'rfp/domain/DistanceUnit'
import { formatDistanceUnitShorthand } from 'rfp/presenters/rfpPresenter'

import { formatFullAppointment } from '../appointments/presenters/appointmentPresenter'
import { type Option } from '../domain/Option'
import { type Stop, hasAddress, hasDate, isDelivery, isPickup } from '../domain/Stop'
import { formatDate as formatDateUtil, isDateOnly, parseISO } from '../utils/date'
import { isString, isNotEmpty, join } from '../utils/string'

export function formatAddress(stop: Stop): string {
  if (hasAddress(stop)) {
    const location = formatLocation(stop)
    return stop.address.replace(new RegExp(`, ${location}$`), '')
  }

  return ''
}

export function formatDate(stop: Stop, options?: { format?: string; fallback?: string }): string {
  if (hasDate(stop)) {
    const date = isDateOnly(stop.date) ? `${stop.date}T23:59:59.999Z` : stop.date
    return formatDateUtil(parseISO(date), options?.format ?? 'EEE, MMM dd')
  }

  return options?.fallback ?? ''
}

export function formatDateAndTime(stop: Stop): string {
  return join([formatDate(stop), formatTime(stop)], ' ')
}

export function formatDescription({ city, state }: Stop): string {
  return join([city, state])
}

export function formatDescriptionWithZip({ city, state, zipCode }: Stop): string {
  return join([city, state, zipCode])
}

export function formatLocation(stop: Stop): string {
  const stateAndZip = join([stop.state, stop.zipCode], ' ')
  return join([stop.city, stateAndZip, stop.country])
}

export function formatLocationOrAnywhere(stop?: Stop): string {
  return stop == null ? 'Anywhere' : formatLocation(stop)
}

export function formatOptionLocation(stop: Stop): Option<string> {
  const location = formatLocation(stop)
  return { label: location, value: location }
}

export function formatTime(stop: Stop, options?: { format?: string; fallback?: string }): string {
  if (hasDate(stop) && !isDateOnly(stop.date)) {
    return formatDateUtil(parseISO(stop.date), options?.format ?? 'HH:mm')
  }

  return options?.fallback ?? ''
}

export function formatType(index: number, stops: Array<Stop>): string {
  if (isPickup(index)) {
    return 'Pickup'
  } else if (isDelivery(index, stops)) {
    return 'Delivery'
  } else {
    return 'Stop'
  }
}

export function formatZip({ zipCode }: Stop): string | undefined {
  if (isString(zipCode) && isNotEmpty(zipCode)) {
    return `ZIP ${zipCode}`
  }
}
export function formatLaneDistance(distance: number | undefined, distanceUnit: DistanceUnit) {
  return `${distance} ${formatDistanceUnitShorthand(distanceUnit)}`
}

export function formatStopToOption(stop: Stop): Option {
  const { city, state, country } = stop

  const address = `${city}, ${state}, ${country}`
  return {
    label: address,
    value: address,
  }
}

export function formatDateOrAppointment(stop: Stop) {
  if (stop.appointment) {
    return formatFullAppointment(stop.appointment)
  }
  return formatDate(stop, {
    format: 'MMM dd',
    fallback: 'Pending appointment',
  })
}
